import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useCart } from '@/context/CartContext';
import { CheckCircle, XCircle, Clock, ArrowRight, FileText, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking'); // checking, success, failed, pending
  const [order, setOrder] = useState(null);
  const sessionId = searchParams.get('session_id');

  // clearCart gets a new identity every time the cart provider re-renders. It used to
  // be an effect dependency, so every clearCart() re-ran this effect and started a new
  // polling chain: an endless loop of status checks while the page stayed open.
  // Keep the latest clearCart in a ref and run the effect once per session instead.
  const clearCartRef = useRef(clearCart);
  clearCartRef.current = clearCart;

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let cancelled = false;
    let timer = null;
    let attempts = 0;
    let errors = 0;

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (cancelled) return;

        if (response.data.payment_status === 'paid') {
          setStatus('success');
          clearCartRef.current();
          // Fetch order details
          if (response.data.order_id) {
            const orderResponse = await axios.get(`${API}/orders/${response.data.order_id}`);
            if (!cancelled) setOrder(orderResponse.data);
          }
        } else if (response.data.status === 'expired') {
          setStatus('failed');
        } else if (attempts < 5) {
          // Continue polling
          attempts += 1;
          timer = setTimeout(pollPaymentStatus, 2000);
        } else {
          // Max polls reached, show pending
          setStatus('pending');
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error checking payment status:', error);
        if (errors < 3) {
          errors += 1;
          timer = setTimeout(pollPaymentStatus, 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    pollPaymentStatus();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId]);

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="checkout-checking">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-600">Verifying your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="checkout-failed">
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              Payment Failed
            </h1>
            <p className="text-slate-600 mb-4">
              We couldn't process your payment. Don't worry - our team has been notified and will contact you shortly to help complete your order.
            </p>
            <p className="text-sm text-slate-500 mb-6">
              You should also receive an email with more details. If you have any questions, please contact us at sales@gringogazette.com
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/cart">
                <Button className="bg-navy-900 hover:bg-navy-800" data-testid="try-again-btn">
                  Try Again
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="checkout-pending">
        <Card className="max-w-lg w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
              Payment Processing
            </h1>
            <p className="text-slate-600 mb-6">
              Your payment is still being processed. You will receive a confirmation email once completed.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/orders">
                <Button className="bg-navy-900 hover:bg-navy-800">
                  Check Order Status
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline">
                  Continue Shopping
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 md:py-16" data-testid="checkout-success">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <Card className="mb-8">
            <CardContent className="pt-8 pb-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">
                Payment Successful!
              </h1>
              <p className="text-slate-600 text-lg">
                Thank you for your order. We've received your payment.
              </p>
            </CardContent>
          </Card>

          {/* Order Details */}
          {order && (
            <Card className="mb-8">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-xl font-bold text-slate-900">
                    Order Details
                  </h2>
                  <span className="text-sm text-slate-500">
                    Order #{order.id.slice(0, 8)}
                  </span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Customer</span>
                    <span className="text-slate-900">{order.customer_name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Email</span>
                    <span className="text-slate-900">{order.customer_email}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Status</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Paid
                    </span>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold text-slate-900 mb-3">Items</h3>
                  <div className="space-y-2">
                    {order.items.map((item, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="text-slate-600">
                          {item.product_name} x {item.quantity}
                        </span>
                        <span className="text-slate-900 font-medium">
                          {formatPrice(item.price * item.quantity)}
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t mt-3 pt-3 flex justify-between">
                    <span className="font-semibold text-slate-900">Total</span>
                    <span className="font-heading text-xl font-bold text-navy-900">
                      {formatPrice(order.total_amount)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Next Steps */}
          <Card className="mb-8">
            <CardContent className="pt-6">
              <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">
                What's Next?
              </h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="h-5 w-5 text-navy-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Check Your Email</h3>
                    <p className="text-sm text-slate-600">
                      You'll receive a confirmation email with your order details and next steps.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 bg-navy-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-navy-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">What Happens Next?</h3>
                    <p className="text-sm text-slate-600">
                      Our team will reach out to you shortly if there are any questions about your advertisement. If you have any questions feel free to reach us at ads@gringogazette.com
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/orders">
              <Button variant="outline" className="w-full sm:w-auto" data-testid="view-orders-btn">
                View Order History
              </Button>
            </Link>
            <Link to="/products">
              <Button className="w-full sm:w-auto bg-navy-900 hover:bg-navy-800" data-testid="continue-shopping-btn">
                Continue Shopping
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
